import Rating from './Rating';

export default class DaySummaryAnalyzer {
    
    constructor(dayData, data, idealConditions) {
        this.dayData = dayData;
        this.data = data;
        this.idealConditions = idealConditions;
        this.ratings = this.calculateAllRatings();
    }

    calculateAllRatings() {
        return this.dayData.map(({ index, dateTime }) => {
            const rating = new Rating({
                waveheight: this.data.wave_height?.[index],
                swellperiod: this.data.swell_wave_period?.[index],
                swelldirection: this.data.swell_wave_direction?.[index],
                windspeed: this.data.wind_speed_10m?.[index],
                winddirection: this.data.wind_direction_10m?.[index]
            }, this.idealConditions);

            return {
                time: dateTime.hour,
                rating,
                score: rating.score,
                index
            };
        });
    }

    getBestTime() {
        const best = this.ratings.reduce((max, current) => 
            current.score > max.score ? current : max
        );
        return best;
    }

    getWorstTime() {
        const worst = this.ratings.reduce((min, current) => 
            current.score < min.score ? current : min
        );
        return worst;
    }

    getAverageScore() {
        const total = this.ratings.reduce((sum, r) => sum + r.score, 0);
        return (total / this.ratings.length).toFixed(1);
    }

    getWaveTrend() {
        const waves = this.dayData.map(({ index }) => 
            Number(this.data.wave_height?.[index] || 0)
        );
        
        const firstHalf = waves.slice(0, Math.floor(waves.length / 2));
        const secondHalf = waves.slice(Math.floor(waves.length / 2));
        
        const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
        const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
        
        const max = Math.max(...waves);
        const min = Math.min(...waves);
        const maxTime = this.dayData[waves.indexOf(max)]?.dateTime.hour;
        const minTime = this.dayData[waves.indexOf(min)]?.dateTime.hour;

        if (Math.abs(avgSecond - avgFirst) < 0.2) {
            return `Wave heights remain fairly consistent throughout the day, ranging from ${min.toFixed(1)}m to ${max.toFixed(1)}m.`;
        } else if (avgSecond > avgFirst) {
            return `Waves build throughout the day, starting at ${min.toFixed(1)}m around ${minTime} and peaking at ${max.toFixed(1)}m around ${maxTime}.`;
        } else {
            return `Waves drop as the day progresses, starting at ${max.toFixed(1)}m around ${maxTime} and decreasing to ${min.toFixed(1)}m by ${minTime}.`;
        }
    }

    getWindTrend() {
        const windSpeeds = this.dayData.map(({ index }) => 
            Number(this.data.wind_speed_10m?.[index] || 0)
        );
        
        const max = Math.max(...windSpeeds);
        const min = Math.min(...windSpeeds);
        const maxTime = this.dayData[windSpeeds.indexOf(max)]?.dateTime.hour;
        const minTime = this.dayData[windSpeeds.indexOf(min)]?.dateTime.hour;
        
        const { preferred_wind_speed_min, preferred_wind_speed_max } = this.idealConditions;
        const idealMin = Number(preferred_wind_speed_min);
        const idealMax = Number(preferred_wind_speed_max);

        let qualityNote = '';
        if (max <= idealMax + 2 && min >= idealMin - 2) {
            qualityNote = ' Wind conditions are favorable throughout the day.';
        } else if (max > idealMax + 5) {
            qualityNote = ` Wind gets too strong around ${maxTime}, which may affect conditions.`;
        } else if (min < idealMin - 5) {
            qualityNote = ` Wind is quite light around ${minTime}.`;
        }

        return `Wind speeds range from ${min} km/h at ${minTime} to ${max} km/h at ${maxTime}.${qualityNote}`;
    }

    getSwellTrend() {
        const periods = this.dayData.map(({ index }) => 
            Number(this.data.swell_wave_period?.[index] || 0)
        );
        
        const avg = (periods.reduce((a, b) => a + b, 0) / periods.length).toFixed(1);
        const max = Math.max(...periods);
        const min = Math.min(...periods);

        if (max - min < 2) {
            return `Swell period stays consistent around ${avg}s throughout the day, indicating steady swell energy.`;
        } else {
            return `Swell period varies from ${min}s to ${max}s throughout the day, with an average of ${avg}s.`;
        }
    }

    getPrimeTimeWindows() {
        // Find consecutive hours with score >= 3.5
        const primeWindows = [];
        let currentWindow = [];

        this.ratings.forEach((r, i) => {
            if (r.score >= 3.5) {
                currentWindow.push(r);
            } else {
                if (currentWindow.length > 0) {
                    primeWindows.push(currentWindow);
                    currentWindow = [];
                }
            }
        });
        
        if (currentWindow.length > 0) {
            primeWindows.push(currentWindow);
        }

        if (primeWindows.length === 0) {
            return null;
        }

        return primeWindows.map(window => {
            const start = window[0].time;
            const end = window[window.length - 1].time;
            const avgScore = (window.reduce((sum, r) => sum + r.score, 0) / window.length).toFixed(1);
            
            if (window.length === 1) {
                return `${start} (${avgScore}/5)`;
            } else {
                return `${start} - ${end} (${avgScore}/5)`;
            }
        });
    }

    getDaySummary() {
        const best = this.getBestTime();
        const avgScore = this.getAverageScore();
        const primeWindows = this.getPrimeTimeWindows();

        let summary = '';

        // Overall assessment
        if (avgScore >= 4.0) {
            summary += `🌟 Excellent day overall with an average rating of ${avgScore}/5 stars. `;
        } else if (avgScore >= 3.0) {
            summary += `✅ Good day with an average rating of ${avgScore}/5 stars. `;
        } else if (avgScore >= 2.0) {
            summary += `⚠️ Fair conditions with an average rating of ${avgScore}/5 stars. `;
        } else {
            summary += `❌ Challenging day with an average rating of ${avgScore}/5 stars. `;
        }

        // Best time
        summary += `Peak conditions occur at ${best.time} with a ${best.score}/5 star rating. `;

        // Prime time windows
        if (primeWindows && primeWindows.length > 0) {
            summary += `\n\n⏰ Prime surf windows: ${primeWindows.join(', ')}`;
        } else {
            summary += `\n\n⏰ No prime windows (3.5+ stars) today, but ${best.time} offers the best opportunity.`;
        }

        // Trends
        summary += `\n\n${this.getWaveTrend()}`;
        summary += `\n\n${this.getWindTrend()}`;
        summary += `\n\n${this.getSwellTrend()}`;

        return summary;
    }
}
